import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, switchMap, take, takeUntil } from 'rxjs/operators';

import {
  Company,
  DashpivotEvent,
  EventNotifierService,
  EventTypes,
} from '@site-mate/dashpivot-shared-library';

import { SegmentService } from 'app/segment/segment.service';
import { CompanyService } from 'app/shared/service/company.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { ImageManipulationService } from 'app/shared/service/image-manipulation/image-manipulation.service';
import { MetaDataService } from 'app/shared/service/meta-data.service';
import { RouteParamsService } from 'app/shared/service/route-params.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';

@Component({
  selector: 'cc-company-brand-settings',
  templateUrl: 'company-brand-settings.component.html',
  styleUrls: ['../../home-settings.component.scss'],
})
export class CompanyBrandSettingsComponent implements OnInit, OnDestroy {
  unsub = new Subject<void>();
  isUploading: boolean;
  originalColor: string;
  isEditColor: boolean;
  hasEditPermission: boolean;

  isCompanyLoading: boolean;

  company: Company;

  logo: string;
  color: string;

  photoToUploadUrl: string;
  photoToUploadFile: any;

  constructor(
    private readonly metaDataService: MetaDataService,
    private readonly toastr: ToastrService,
    private readonly errorHandler: ErrorHandler,
    private readonly segmentService: SegmentService,
    private readonly routeParamsService: RouteParamsService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly companyService: CompanyService,
    private readonly userService: UserService,
    private readonly imageManipulationService: ImageManipulationService,
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(
        distinctUntilChanged(),
        switchMap((params) => this.companyService.getCompany(params.companyId)),
        takeUntil(this.unsub),
      )
      .subscribe(
        (company: Company) => {
          this.company = company;
          this.logo = company.logo;
          this.color = company.colorCode;
          this.isCompanyLoading = false;

          this.photoToUploadUrl = company.logo;
          this.originalColor = company.colorCode;
          this.isUploading = false;
        },
        (error) => {
          this.errorHandler.handle(error);
        },
      );
    this.hasEditPermission = this.userService.isCompanyControllerOrAbove();
  }

  ngOnDestroy(): void {
    this.unsub.next();
    this.unsub.complete();
  }

  get canSaveSettings() {
    return !this.isUploading && (this.logo !== this.photoToUploadUrl || this.color !== this.originalColor);
  }

  onPhotoSelected(event: Event) {
    const eventTarget = event.target as HTMLInputElement;
    const fileList = eventTarget.files;

    if (fileList.length > 0) {
      const file = fileList[0];

      this.imageManipulationService
        .resizeImage(file, 2000, 2000)
        .then((resizedFile) => {
          const formData = new FormData();
          formData.append('file', resizedFile);

          const reader = new FileReader();
          reader.readAsDataURL(resizedFile);

          reader.onload = (e: any) => {
            this.photoToUploadUrl = e.target.result;
          };

          this.photoToUploadFile = resizedFile;
          eventTarget.value = null;
        })
        .catch((error) => {
          this.errorHandler.handleForDebug(error);
        });
    }
  }

  uploadLogo() {
    const formData = new FormData();
    formData.append('file', this.photoToUploadFile);
    combineLatest([
      this.metaDataService.uploadLogo(this.company?.id, formData),
      this.routeParamsService.get('company').pipe(take(1)),
    ]).subscribe(
      ([res, company]) => {
        void EventNotifierService.notify(
          new DashpivotEvent(EventTypes.OrganisationSettingApplied, { Context: 'Logo Uploaded' }),
          this.segmentService,
        );
        this.toastr.successByKey('logoAdded');
        this.logo = res.logo;
        this.photoToUploadUrl = res.logo;

        this.routeParamsService.set('company', { ...company, logo: res.logo });
        this.isUploading = false;
      },
      (error) => {
        this.errorHandler.handle(error);
      },
    );
  }

  editColor() {
    this.isEditColor = true;
  }

  onColorSelected() {
    this.isEditColor = false;
  }

  saveColor() {
    this.metaDataService.updateColorCode(this.company?.id, this.color).subscribe(
      (res) => {
        this.isEditColor = false;
        void EventNotifierService.notify(
          new DashpivotEvent(EventTypes.OrganisationSettingApplied, { Context: 'Colour Scheme Selected' }),
          this.segmentService,
        );
        this.toastr.successByKey('colorUpdated');
        this.color = res.colorCode;
        this.originalColor = res.colorCode;
      },
      (error) => {
        this.errorHandler.handle(error);
      },
    );
  }

  cancelEditColor() {
    this.color = this.originalColor;
    this.isEditColor = false;
  }

  onSave() {
    if (this.originalColor !== this.color) {
      this.saveColor();
    }

    if (this.logo !== this.photoToUploadUrl) {
      this.isUploading = true;
      this.uploadLogo();
    }
  }
}
